import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ApolloProvider } from '@apollo/client';
import client from 'src/api/graphqlClient';
import * as Sentry from '@sentry/react';
import { initializeRUM } from 'src/services/rum';
import { RUM_API } from 'src/config-global';
import App from './App';

// Tailwind CSS - must be imported for Tailwind/shadcn components to work
import 'src/styles/globals.css';

// ----------------------------------------------------------------------

if (import.meta.env.VITE_NODE_ENV === 'prod' || import.meta.env.VITE_NODE_ENV === 'dev') {
  Sentry.init({
    dsn: 'https://b9836f8b340e5b68cbb17498c4ef5e02@o4508579067723776.ingest.de.sentry.io/4508579071000656',
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Initialize AWS RUM for performance and error monitoring
if (RUM_API.appMonitorId) {
  initializeRUM({
    appMonitorId: RUM_API.appMonitorId,
    identityPoolId: RUM_API.identityPoolId || '',
    region: RUM_API.region || 'eu-central-1',
    applicationVersion: RUM_API.applicationVersion,
  });
}

// Recover from stale-deployment chunk 404s: when a cached main bundle references
// a chunk hash the CDN no longer serves, reload to pick up fresh assets.
// preventDefault() must come first — Vite checks !e.defaultPrevented after dispatch
// and rethrows the chunk-load error if not cancelled.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  try {
    if (sessionStorage.getItem('vite-preload-reload')) return;
    sessionStorage.setItem('vite-preload-reload', '1');
  } catch {
    // sessionStorage unavailable (Safari private mode, quota exceeded) —
    // still better to reload once than to leave the app in a broken state.
  }
  window.location.reload();
});

// Clear the reload guard on successful load so a later deploy in the same
// tab session can still recover — the guard only blocks back-to-back failures.
window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem('vite-preload-reload');
  } catch {
    /* noop */
  }
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <HelmetProvider>
    <ApolloProvider client={client}>
      <BrowserRouter>
        <Suspense>
          <App />
        </Suspense>
      </BrowserRouter>
    </ApolloProvider>
  </HelmetProvider>
);
